import React, { useEffect, useRef, useState } from "react";
import { trophies } from "@/lib/trophies";

const skinOf = () =>
  trophies.has("platinum") ? "prism" : trophies.has("konami") ? "gold" : "";

export default function MolecularCursor() {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const skinRef = useRef(skinOf());
  const [skin, setSkin] = useState(skinRef.current);

  useEffect(() => {
    const un = trophies.subscribe(() => {
      const s = skinOf();
      skinRef.current = s;
      setSkin(s);
    });
    return un;
  }, []);

  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return undefined;
    setActive(true);
    document.documentElement.classList.add("iv-no-cursor");
    const pos = { x: -100, y: -100 };
    const cur = { x: -100, y: -100 };
    let raf;

    const onMove = (e) => {
      pos.x = e.clientX;
      pos.y = e.clientY;
      const el = ref.current;
      if (!el) return;
      const t = e.target;
      const interactive = t.closest?.("button, a, [role='button'], select, input[type='range'], .iv-tile");
      const typing = t.closest?.("input:not([type='range']), textarea");
      el.classList.toggle("iv-cursor-hover", !!interactive);
      el.classList.toggle("iv-cursor-hidden", !!typing);
    };
    const decorate = (p) => {
      if (skinRef.current === "prism") {
        p.classList.add("iv-ion-prism");
        p.style.filter = `hue-rotate(${Math.floor(Math.random() * 360)}deg) saturate(1.5)`;
      } else if (skinRef.current === "gold") {
        p.classList.add("iv-ion-gold");
      }
    };
    const burst = (x, y) => {
      if (ref.current?.classList.contains("iv-cursor-hidden")) return;
      const n = 8 + Math.floor(Math.random() * 4);
      for (let i = 0; i < n; i += 1) {
        const p = document.createElement("span");
        p.className = "iv-ion iv-ion-burst";
        const s = 2 + Math.random() * 3;
        const a = (Math.PI * 2 * i) / n + Math.random() * 0.6;
        const d = 18 + Math.random() * 22;
        p.style.cssText = `left:${x}px;top:${y}px;width:${s}px;height:${s}px;--dx:${Math.cos(a) * d}px;--dy:${Math.sin(a) * d}px;`;
        decorate(p);
        p.addEventListener("animationend", () => p.remove());
        document.body.appendChild(p);
      }
    };

    const onDown = (e) => {
      ref.current?.classList.add("iv-cursor-down");
      burst(e.clientX, e.clientY);
    };
    const onUp = () => ref.current?.classList.remove("iv-cursor-down");
    const onLeave = () => ref.current?.classList.add("iv-cursor-hidden");
    const onEnter = () => ref.current?.classList.remove("iv-cursor-hidden");

    const spawn = { x: -100, y: -100 };
    let ions = 0;
    const spawnIon = (x, y) => {
      if (ions > 28 || ref.current?.classList.contains("iv-cursor-hidden")) return;
      ions += 1;
      const p = document.createElement("span");
      p.className = "iv-ion";
      const s = 2.5 + Math.random() * 2.5;
      p.style.cssText = `left:${x}px;top:${y}px;width:${s}px;height:${s}px;--dx:${Math.random() * 26 - 13}px;--dy:${Math.random() * 26 - 13}px;`;
      decorate(p);
      p.addEventListener("animationend", () => {
        p.remove();
        ions -= 1;
      });
      document.body.appendChild(p);
    };

    const loop = () => {
      cur.x += (pos.x - cur.x) * 0.4;
      cur.y += (pos.y - cur.y) * 0.4;
      if (ref.current) ref.current.style.transform = `translate3d(${cur.x}px, ${cur.y}px, 0)`;
      const dx = cur.x - spawn.x;
      const dy = cur.y - spawn.y;
      if (dx * dx + dy * dy > 130) {
        spawn.x = cur.x;
        spawn.y = cur.y;
        spawnIon(cur.x, cur.y);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointerup", onUp);
    document.documentElement.addEventListener("mouseleave", onLeave);
    document.documentElement.addEventListener("mouseenter", onEnter);
    return () => {
      cancelAnimationFrame(raf);
      document.documentElement.classList.remove("iv-no-cursor");
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      document.documentElement.removeEventListener("mouseenter", onEnter);
    };
  }, []);

  if (!active) return null;
  return (
    <div
      ref={ref}
      className={`iv-molecule-cursor${skin ? ` iv-cursor-${skin}` : ""}`}
      aria-hidden="true"
      data-testid="molecular-cursor"
    >
      <div className="iv-mc-inner">
        <span className="iv-cube">
          <i className="iv-cube-face cf" />
          <i className="iv-cube-face cb" />
          <i className="iv-cube-face cl" />
          <i className="iv-cube-face cr" />
          <i className="iv-cube-face ct" />
          <i className="iv-cube-face cd" />
        </span>
      </div>
    </div>
  );
}
